import React, { useCallback, useState } from 'react';
import { IconButton } from '@atlaskit/button/new';
import DropdownMenu, { DropdownItemGroup, type OnOpenChangeArgs } from '@atlaskit/dropdown-menu';
import LinkIcon from '@atlaskit/icon/core/migration/link';
import ChevronDownIcon from '@atlaskit/icon/utility/migration/chevron-down';
import AKTooltip from '@atlaskit/tooltip';
import { fg } from '@atlassian/jira-feature-gating';
import { fireUIAnalytics, useAnalyticsEvents } from '@atlassian/jira-product-analytics-bridge';
import type { QuickAddItemToRender } from '../../types.tsx';
import DropdownListItem from './dropdown-list-item.tsx';

export const dropDownListButtonSubject = 'linkDropdownListButton';
export const dropDownListButtonSubjectId = 'moreLinksDropdownToggled';

export const dropDownListItemSubject = 'linkDropdownListItem';
export const dropDownListItemSubjectId = 'createLink';

export type Props = {
	placement?: 'bottom-start' | 'bottom-end';
	tooltip: string;
	actions: QuickAddItemToRender[];
	isCompactMode?: boolean;
	isQuickActionsListView?: boolean;
};

const DropdownListButton = (props: Props) => {
	const [isExpanded, setIsExpanded] = useState(false);
	const { placement, actions, isCompactMode, tooltip, isQuickActionsListView } = props;
	const { createAnalyticsEvent } = useAnalyticsEvents();

	const triggerButton = useCallback(
		// @ts-expect-error - TS7031 - Binding element 'triggerRef' implicitly has an 'any' type.
		({ triggerRef, ...triggerProps }) => {
			const dropdownIconButton = (
				<IconButton
					{...triggerProps}
					label={tooltip}
					aria-expanded={isExpanded}
					icon={isCompactMode ? LinkIcon : ChevronDownIcon}
					/* eslint-disable-next-line jira/integration/test-id-by-folder-structure */
					testId="issue.views.issue-base.foundation.quick-add.link-button.ui.link-dropdown-button"
					ref={triggerRef}
					interactionName="auto-generated.issue.views.issue-base.foundation.quick-add.link-button.ui.link-dropdown-button.button"
				/>
			);

			return tooltip !== undefined ? (
				<AKTooltip content={tooltip} position="bottom" hideTooltipOnClick>
					{dropdownIconButton}
				</AKTooltip>
			) : (
				dropdownIconButton
			);
		},
		[isExpanded, isCompactMode, tooltip],
	);

	const onOpenChange = ({ isOpen }: OnOpenChangeArgs) => {
		setIsExpanded(isOpen);
		fireUIAnalytics(
			createAnalyticsEvent({ action: 'toggled', actionSubject: 'dropdownMenu' }),
			dropDownListButtonSubjectId,
			{
				isOpen,
				name: dropDownListButtonSubject,
			},
		);
	};

	if (isQuickActionsListView && fg('quick_actions_menu_ga')) {
		return actions.map((item) => <DropdownListItem key={item.id} item={item} />);
	}
	return (
		<DropdownMenu
			key="web-link-actions-dropdown"
			placement={placement}
			trigger={triggerButton}
			onOpenChange={onOpenChange}
			shouldRenderToParent
		>
			<DropdownItemGroup>
				{actions.map((item) => (
					<DropdownListItem key={item.id} item={item} />
				))}
			</DropdownItemGroup>
		</DropdownMenu>
	);
};

export default DropdownListButton;
